'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAdPersonalizationRestricted, waitForConsentApi } from '@/src/lib/datagrail/consent';
import {
  getSrpAdDevice,
  getSrpAdSlotPath,
  SRP_AD_SIZES,
  SRP_AD_SLOTS,
  type SrpAdDevice,
  type SrpAdSlot,
} from './constants';
import { type GoogleTagPubAds, type GoogleTagSlot, queueGptCommand } from './gpt';
import type { SrpAdTargeting } from './targeting';

// Legacy won't refresh a slot more than once per 10s (`ads.ts` shouldUpdateAd).
// Matching it keeps a map drag from burning through requests faster than they
// can accrue the 1s in-view that makes an impression billable.
const MIN_REFRESH_INTERVAL_MS = 10_000;

function readAdDevice(): SrpAdDevice {
  // Measured the way legacy did (min of the two widths, so scrollbars don't count).
  return getSrpAdDevice(Math.min(window.innerWidth, document.documentElement.clientWidth) || window.innerWidth);
}

/**
 * The `targeting` patch that turns `previous` into `next`.
 *
 * `Slot.setConfig({ targeting })` merges, so a key dropped from the new search
 * (`bd` once the bedroom filter clears, say) would otherwise stay on the slot
 * and be sent to GAM forever. Nulling it is how the Config API deletes a key.
 */
export function buildTargetingUpdate(
  next: SrpAdTargeting,
  previous: SrpAdTargeting | null,
): Record<string, string | Array<string> | null> {
  const update: Record<string, string | Array<string> | null> = { ...next };
  for (const key of Object.keys(previous ?? {})) {
    if (!(key in next)) update[key] = null;
  }
  return update;
}

function applyPrivacySettings(pubads: GoogleTagPubAds): void {
  // Set both directions every time: `pubads()` is a page-global singleton, so a
  // restriction left over from an earlier mount would otherwise keep later
  // requests non-personalized after the user grants consent.
  const isRestricted = isAdPersonalizationRestricted();
  pubads.setRequestNonPersonalizedAds(isRestricted ? 1 : 0);
  pubads.setPrivacySettings({ restrictDataProcessing: isRestricted });
}

/**
 * Defines the given SRP GAM slots, then keeps them earning: a later search
 * re-requests them in place via `pubads().refresh` rather than tearing them
 * down, mirroring legacy's `defineAd`/`refreshAd` pair.
 *
 * Only pass slots whose containers render — GPT can't display into a div that
 * isn't there. A null `targeting` holds the *first* ad request back until the
 * key/values are ready; once slots are live a null no longer tears them down,
 * since adtags goes pending again on every viewport change.
 *
 * `searchIdentity` identifies the search itself (area + slug + non-viewport
 * filters). A change to it forces a refresh past the throttle, which is what
 * legacy does with `AdActions.refreshAd(true)` on area resolution, search-bar
 * selection, and listing-type changes.
 *
 * `containerKey` identifies which tree the containers render in. GPT holds the
 * element it displayed into, so a tree swap that remounts the same container ids
 * (grid ↔ feed) has to destroy and redefine — refreshing would render into the
 * detached node.
 */
export function useSrpGoogleAds(
  activeSlots: ReadonlyArray<SrpAdSlot>,
  targeting: SrpAdTargeting | null,
  adListingType: string,
  searchIdentity: string,
  containerKey = '',
): void {
  const activeSlotIds = activeSlots.map((slot) => slot.id).join(',');
  // Effects key off this rather than the array, which is rebuilt each render.
  const slotSetKey = `${containerKey}:${activeSlotIds}`;
  const targetingKey = targeting === null ? null : JSON.stringify(targeting);

  const targetingRef = useRef(targeting);
  targetingRef.current = targeting;
  const searchIdentityRef = useRef(searchIdentity);
  searchIdentityRef.current = searchIdentity;

  // The slots GPT currently holds, and what was last asked for on them.
  const liveSlotsRef = useRef<Array<GoogleTagSlot>>([]);
  // The object, not just its JSON: `setConfig` merges, so removing a key needs
  // the previous key set to diff against.
  const lastAppliedTargetingRef = useRef<SrpAdTargeting | null>(null);
  const lastRequestedTargetingRef = useRef<string | null>(null);
  const lastRequestedSearchRef = useRef<string | null>(null);
  const lastRequestAtRef = useRef(0);
  const hasTargeting = targeting !== null;

  // Started on mount so the poll overlaps the gpt.js download instead of
  // queueing behind it — the two waits used to be serial, which pushed the
  // first ad request out by however long DataGrail took to answer.
  const consentReadyRef = useRef<Promise<boolean> | null>(null);
  const getConsentReady = useCallback(() => (consentReadyRef.current ??= waitForConsentApi()), []);
  useEffect(() => {
    void getConsentReady();
  }, [getConsentReady]);

  const slotsToDefine = useMemo(
    () => SRP_AD_SLOTS.filter((slot) => activeSlotIds.split(',').includes(slot.id)),
    [activeSlotIds],
  );

  // Legacy recomputes the placement on every refresh and force-destroys when it
  // changed, so a rotated tablet stops reporting against the phone unit.
  const [adDevice, setAdDevice] = useState<SrpAdDevice | null>(() =>
    typeof window === 'undefined' ? null : readAdDevice(),
  );
  useEffect(() => {
    // Same value short-circuits the re-render, so this stays cheap while dragging.
    const sync = () => setAdDevice(readAdDevice());
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  // Definition and teardown are split across two effects on purpose. Targeting
  // gates only this one, so it dropping back to null — which adtags does on
  // every viewport change — can't tear a live slot down; that was what made a
  // map drag destroy the ad and restart its viewability clock.
  useEffect(() => {
    if (slotsToDefine.length === 0 || !hasTargeting || adDevice === null) return;
    // Already serving this slot set; a targeting change refreshes it below.
    if (liveSlotsRef.current.length > 0) return;

    let cancelled = false;
    const definedSlots: Array<GoogleTagSlot> = [];

    // Hold the first ad request until DataGrail can report consent, so a
    // non-consenting user never gets a personalized one. If DataGrail never
    // loads, `isAdPersonalizationRestricted` leaves the request personalized —
    // see the fail-open rationale there.
    void getConsentReady().then(() => {
      if (cancelled) return;

      queueGptCommand((gpt) => {
        if (cancelled) return;

        const appliedTargeting = targetingRef.current ?? {};
        for (const { id, format } of slotsToDefine) {
          const slot = gpt.defineSlot(getSrpAdSlotPath(adDevice, format, adListingType), SRP_AD_SIZES, id);
          if (!slot) continue;
          // Fresh slot, so nothing to null out.
          slot.setConfig({ targeting: buildTargetingUpdate(appliedTargeting, null) });
          definedSlots.push(slot);
        }

        const pubads = gpt.pubads();
        for (const slot of definedSlots) slot.addService(pubads);

        applyPrivacySettings(pubads);

        gpt.setConfig({ singleRequest: true, collapseDiv: 'ON_NO_FILL' });
        pubads.enableAsyncRendering();
        pubads.set('page_url', window.location.href);

        gpt.enableServices();
        for (const { id } of slotsToDefine) gpt.display(id);

        liveSlotsRef.current = definedSlots;
        lastAppliedTargetingRef.current = appliedTargeting;
        lastRequestedTargetingRef.current = JSON.stringify(appliedTargeting);
        lastRequestedSearchRef.current = searchIdentityRef.current;
        lastRequestAtRef.current = Date.now();
      });
    });

    // Only abandons an in-flight definition — the teardown effect owns destroying.
    return () => {
      cancelled = true;
    };
  }, [adDevice, adListingType, getConsentReady, hasTargeting, slotSetKey, slotsToDefine]);

  // Destroys only when the ad unit path or the slot set itself changes. React
  // runs every cleanup before any effect, so this releases the container ids
  // before the effect above redefines against them.
  useEffect(() => {
    return () => {
      const slots = liveSlotsRef.current;
      liveSlotsRef.current = [];
      lastAppliedTargetingRef.current = null;
      lastRequestedTargetingRef.current = null;
      lastRequestedSearchRef.current = null;
      if (slots.length === 0) return;
      // Destroy rather than clear: a slot left defined against a container id
      // blocks `defineSlot` from redefining it when the SRP remounts.
      queueGptCommand((gpt) => gpt.destroySlots(slots));
    };
  }, [adDevice, adListingType, slotSetKey]);

  useEffect(() => {
    // A new search jumps the queue the way legacy's `refreshAd(true)` does. A
    // map drag only moves the bounding box, which `searchIdentity` excludes, so
    // panning still goes through the throttled targeting path below.
    const isForced = searchIdentity !== lastRequestedSearchRef.current;
    if (!isForced && (targetingKey === null || targetingKey === lastRequestedTargetingRef.current)) return;
    // Nothing displayed yet — the define effect will pick up the latest targeting.
    if (liveSlotsRef.current.length === 0) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const requestRefresh = () => {
      const slots = liveSlotsRef.current;
      const appliedTargeting = targetingRef.current;
      // No targeting yet means adtags is still in flight for the new search;
      // leave `lastRequestedSearchRef` alone so this reruns once it lands.
      if (cancelled || slots.length === 0 || !appliedTargeting) return;
      // A redefine may have landed while this waited out the interval; it
      // already requested with current targeting, so there's nothing to refresh.
      if (!isForced && Date.now() - lastRequestAtRef.current < MIN_REFRESH_INTERVAL_MS) return;

      const targetingUpdate = buildTargetingUpdate(appliedTargeting, lastAppliedTargetingRef.current);
      lastAppliedTargetingRef.current = appliedTargeting;
      lastRequestedTargetingRef.current = JSON.stringify(appliedTargeting);
      lastRequestedSearchRef.current = searchIdentityRef.current;
      lastRequestAtRef.current = Date.now();

      queueGptCommand((gpt) => {
        const pubads = gpt.pubads();
        for (const slot of slots) slot.setConfig({ targeting: targetingUpdate });

        applyPrivacySettings(pubads);
        pubads.set('page_url', window.location.href);
        pubads.refresh(slots);
      });
    };

    const sinceLastRequest = Date.now() - lastRequestAtRef.current;
    if (isForced || sinceLastRequest >= MIN_REFRESH_INTERVAL_MS) {
      requestRefresh();
    } else {
      // Trailing edge: refresh with whatever targeting is current once the
      // interval is up, so a search made during the cooldown isn't skipped.
      timer = setTimeout(requestRefresh, MIN_REFRESH_INTERVAL_MS - sinceLastRequest);
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchIdentity, targetingKey]);
}
